// custom-dashboard アップロードのクライアント側検証。
// セキュリティ境界は BE（backend/core/src/handlers/custom_dashboard/upload_validation/）が正で、
// ここでの検証は送信前にエラーへ気付けるようにする UX 上の早期検出に過ぎない。
// 深さ制限・vendor allowlist 突合・description 等の詳細検証は BE に任せる（早期検出の費用対効果）。

import { staticValidate } from './static-validation';

// 各ファイルは「展開時（平文）」と「圧縮時（gzip）」の 2 つの上限を持つ。3 ファイルとも gzip で送るため、
// multipart 全体に掛かる API Gateway HTTP API のペイロード上限 10 MB（ハード制限・引き上げ不可）は
// 圧縮時の上限（合計 9,961,472 bytes）で守る。
// 値は BE（backend/core/src/handlers/custom_dashboard/upload_validation/mod.rs）と
// docs に同値があり、同期して変更すること。
export const HTML_MAX_BYTES = 2 * 1024 * 1024;
export const HTML_COMPRESSED_MAX_BYTES = 384 * 1024;
export const DATA_MAX_BYTES = 20 * 1024 * 1024;
/** 数値中心の data.json は gzip で 2.4 倍程度に縮むため、展開時 20MB でも収まる。 */
export const DATA_COMPRESSED_MAX_BYTES = 9 * 1024 * 1024;
export const DETAILS_MAX_BYTES = 524_288;
export const DETAILS_COMPRESSED_MAX_BYTES = 128 * 1024;
/** BE upload_validation/data.rs の MAX_NAME_CHARS と同値。BE と同期して変更する。 */
export const MAX_NAME_CHARS = 120;

const utf8ByteLength = (text: string): number => new TextEncoder().encode(text).length;

/**
 * bytes を利用者向けの表記にする（1 MB = 1024 KB = 1024 * 1024 bytes）。1MB 以上は MB（小数 1 桁）、
 * 未満は KB（整数）。切り上げて、上限をわずかに超えた値が上限と同じ表示にならないようにする。
 */
export const formatByteSize = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${Math.ceil((bytes / (1024 * 1024)) * 10) / 10}MB`
    : `${Math.ceil(bytes / 1024)}KB`;

/** タブ名（表示名）を検証しエラーメッセージの一覧を返す。 */
export function validateTabName(name: string): string[] {
  const trimmed = name.trim();
  if (trimmed.length === 0 || [...trimmed].length > MAX_NAME_CHARS) {
    return [`タブ名は空白のみを避け ${MAX_NAME_CHARS} 文字以下で入力してください`];
  }
  return [];
}

/**
 * data.json の meta.name を取り出す。新規タブ作成時のタブ名欄の初期値に使う。
 * 表示名の正は sazae 側（custom_dashboard_tabs.name）なので、読めなければ黙って null を返す。
 */
export function extractMetaName(text: string): string | null {
  try {
    const value: unknown = JSON.parse(text);
    if (!isPlainObject(value) || !isPlainObject(value.meta)) return null;
    const name = value.meta.name;
    return typeof name === 'string' && name.trim().length > 0 ? name.trim() : null;
  } catch {
    return null;
  }
}

/** dashboard.html を検証しエラーメッセージの一覧を返す。 */
export function validateDashboardHtml(html: string): string[] {
  const errors: string[] = [];
  const bytes = utf8ByteLength(html);
  if (bytes === 0 || bytes > HTML_MAX_BYTES) {
    errors.push(`dashboard.html は 1〜${HTML_MAX_BYTES} bytes にしてください`);
  }
  errors.push(...staticValidate(html));
  return errors;
}

/** data.json のテキストを検証しエラーメッセージの一覧を返す。 */
export function validateDataJson(text: string, accountId: string): string[] {
  const bytes = utf8ByteLength(text);
  if (bytes === 0 || bytes > DATA_MAX_BYTES) {
    return [`data.json は 1〜${DATA_MAX_BYTES} bytes にしてください`];
  }
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return ['data.json が JSON として解析できません'];
  }
  if (!isPlainObject(value)) {
    return ['data.json のトップレベルは meta と data のちょうど 2 キーにしてください'];
  }
  const errors: string[] = [];
  if (!('meta' in value) || !('data' in value) || Object.keys(value).length !== 2) {
    errors.push('data.json のトップレベルは meta と data のちょうど 2 キーにしてください');
  }
  const meta = value.meta;
  if (!isPlainObject(meta)) {
    if ('meta' in value) errors.push('meta はオブジェクトにしてください');
    return errors;
  }
  if (meta.schemaVersion !== 1) {
    errors.push('schemaVersion は 1 を指定してください');
  }
  if (meta.accountCode !== accountId) {
    errors.push('meta の accountCode がアップロード先のアカウントと一致しません');
  }
  // tabId は sazae が採番するため data.json では検証しない（BE も同様に無視する）。
  // name は任意。表示名の正は sazae 側で、ここでは書かれている場合の形式だけ見る。
  if (meta.name !== undefined) {
    const name = typeof meta.name === 'string' ? meta.name.trim() : '';
    // 文字数は BE（chars().count() = Unicode スカラ値単位）に合わせ code point 単位で数える。
    if (name.length === 0 || [...name].length > MAX_NAME_CHARS) {
      errors.push(`name は空白のみを避け ${MAX_NAME_CHARS} 文字以下にしてください`);
    }
  }
  return errors;
}

/** details.txt のサイズを検証しエラーメッセージの一覧を返す。 */
export function validateDetailsFile(file: File): string[] {
  if (file.size > DETAILS_MAX_BYTES) {
    return [`details.txt は ${DETAILS_MAX_BYTES} bytes 以下にしてください`];
  }
  return [];
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
